import React, { useState } from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  usePagination,
} from "react-table";
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/solid";
import { GoSearch } from "react-icons/go";
import { Button, PageButton } from "./shared/Button";
import { classNames } from "./shared/Utils";
import { SortIcon, SortUpIcon, SortDownIcon } from "./shared/Icons";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div className=" ml-auto">
      <div className="mt-4 text-gray-600 md:overflow-x-auto sm:overflow-x-auto focus-within:text-gray-400 flex w-full justify-end py-4 px-4 items-center ">
        <input
          type="text"
          className="border-b bg-transparent border-b-violet-800 relative block w-3/12border-gray placeholder-gray-400 foucs: outline-none focus:border-blue-200 transition-colors"
          value={value || ""}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder="Search"
        />

        <span className="relative flex flex-wrap md:overflow-x-auto sm:overflow-x-auto ">
          <GoSearch size={15} color="#1F78B4" />
        </span>
      </div>
    </div>
  );
}

// This is a custom filter UI for selecting
// a unique option from a list

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id, render },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      options.add(row.values[id]);
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  // return (
  //   <label className="flex gap-x-2 items-baseline">
  //     <span className="text-gray-100 mx-3">{render("Header")}:</span>
  //     <select
  //       className="form-select appearance-none
  //       block
  //       px-4
  //       py-1
  //       text-sm
  //       font-Montserrat
  //       text-gray-700
  //       bg-white bg-clip-padding bg-no-repeat
  //       border border-solid border-gray-100
  //       rounded-lg
  //       transition
  //       ease-in-out
  //       m-0
  //       focus:text-gray-700 focus:border-blue-100 focus:outline-none"
  //       name={id}
  //       id={id}
  //       value={filterValue}
  //       onChange={(e) => {
  //         setFilter(e.target.value || undefined);
  //       }}
  //     >
  //       <option selected>All</option>
  //       {options.map((option, i) => (
  //         <option key={i} value={option}>
  //           {option}
  //         </option>
  //       ))}
  //     </select>
  //   </label>
  // );
}

export function StatusPill({ value }) {
  const status = value ? value.toLowerCase() : "unknown";

  return (
    <span
      className={classNames(
        "px-3 py-1 uppercase leading-wide text-xs rounded-full shadow-sm",
        status.startsWith("active")
          ? "bg-green-100 text-white"
          : status.startsWith("discontinued")
          ? "bg-red-800 text-white"
          : null
      )}
    >
      {status}
    </span>
  );
}

export function AvatarCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 h-10 w-10">
        <img
          className="h-10 w-10 rounded-full"
          src="https://image.shutterstock.com/image-photo/closeup-elegant-mens-shoes-on-260nw-156770609.jpg"
          alt=""
        />
      </div>
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">
          {row.original[column.emailAccessor]}
        </div>
      </div>
    </div>
  );
}
export function ProductCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 h-10 w-10">
        <svg
          className="ml-8 float-right"
          version="1.1"
          id="Uploaded to svgrepo.com"
          xmlns="http://www.w3.org/2000/svg"
          fill="red"
          width="32px"
          height="32px"
          viewBox="0 0 32 32"
        >
          <path
            class="puchipuchi_een"
            d="M30.211,23.98L17.798,3.034c-0.81-1.366-2.787-1.366-3.596,0L1.789,23.98
   C0.999,25.314,1.96,27,3.51,27h24.98C30.04,27,31.001,25.314,30.211,23.98z M16,24c-1.105,0-2-0.895-2-2c0-1.105,0.895-2,2-2
   s2,0.895,2,2C18,23.105,17.105,24,16,24z M18,13.377c0,1.075-0.173,2.143-0.513,3.162l-0.171,0.512C17.127,17.618,16.597,18,16,18
   s-1.127-0.382-1.316-0.949l-0.171-0.512C14.173,15.52,14,14.452,14,13.377v-2.99C14,9.621,14.621,9,15.387,9h1.225
   C17.379,9,18,9.621,18,10.387V13.377z"
          />
        </svg>
      </div>
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">
          {row.original[column.emailAccessor]}
        </div>
      </div>
    </div>
  );
}
export function ImageCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 h-10 w-10">
        <img
          className=" w-10 "
          src="https://image.shutterstock.com/image-photo/closeup-elegant-mens-shoes-on-260nw-156770609.jpg"
          alt=""
        />
      </div>
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">
          {row.original[column.emailAccessor]}
        </div>
      </div>
    </div>
  );
}
export function ProductImageCell({ value, column, row }) {
  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 ">
        <img
          className=" w-10 "
          src="https://image.shutterstock.com/image-photo/closeup-elegant-mens-shoes-on-260nw-156770609.jpg"
          alt=""
        />
      </div>
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">
          {row.original[column.emailAccessor]}
        </div>
      </div>
    </div>
  );
}

function BomTable({ columns, data, text }) {
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters, // useFilters!
    useGlobalFilter,
    useSortBy,
    usePagination // new
  );

  // Render the UI for your table
  return (
    <>
      {/* table */}
      <div className="mt-4 flex flex-col font-Roboto ">
        <div className="-my-2 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full  sm:px-6 lg:px-8">
            <div className="flex flex-wrap overflow-hidden shadow-md mt-8 rounded-3xl bg-white sm:rounded-sm" style={{borderRadius:"12px"}}>
              {text === "Suppliers" && (
                <div className="flex flex-col m-6 absolute font-Montserrat text-base font-semibold text-gray-100">
                  Suppliers
                </div>
              )}
              {text === "Transactions" && (
                <div className="flex flex-col m-6 absolute font-Montserrat text-base font-semibold text-gray-100">
                  Transactions
                </div>
              )}
              {text === "BOM" && (
                <div className="flex flex-col m-6 absolute font-Montserrat text-base font-semibold text-gray-100">
                  Build Of Material
                </div>
              )}
              {/*Filter*/}
            
         
              {/* {headerGroups.map((headerGroup) =>
                    headerGroup.headers.map((column) =>
                      column.Filter ? (
                        <div className="mt-2 sm:mt-0" key={column.id}>
                          {column.render("Filter")}
                        </div>
                      ) : null
                    )
                  )} */}
              <table
                {...getTableProps()}
                className="min-w-full bg-transparent font-Montserrat  divide-y divide-gray-200"
              >
                <thead className=" border-b-8 border-white">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        // Add the sorting props to control sorting. For this example
                        // we can add them into the header props
                        <th
                          scope="col"
                          className="group px-2 py-3 text-center text-sm font-medium  text-gray-400 font-Roboto tracking-wider"
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          <div className="flex items-center justify-between">
                            {column.render("Header")}
                            {/* Add a sort direction indicator */}
                            <span>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <SortDownIcon className="w-4 h-4 text-gray-100" />
                                ) : (
                                  <SortUpIcon className="w-4 h-4 text-gray-100" />
                                )
                              ) : (
                                <SortIcon className="w-4 h-4 text-gray-100 opacity-0 group-hover:opacity-100" />
                              )}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="bg-white">
                  {page.map((row, index) => {
                    // new
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={
                          index % 2 === 0
                            ? "bg-cyan-100 border-b-8 border-white"
                            : "bg-white border-b-8 border-white"
                        }
                      >
                        {row.cells.map((cell) => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className="pr-2 pl-2  whitespace-normal"
                              role="cell"
                            >
                              {cell.column.Cell.name === "defaultRenderer" ? (
                                <div className="text-sm font-semibold text-black">
                                  {cell.render("Cell")}
                                </div>
                              ) : (
                                cell.render("Cell")
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>

                {/* <tfoot className="">
                
                  <div className="text-center justify-center m-auto   py-4 px-3">
                    {/* Filter rows */}
                {/*  <div className="pt-2 px-3 text-center justify-center  m-auto">
                      {/* Pagination */}
                {/* hidden sm:flex-1 sm:flex sm:items-center sm:justify-between */}
                {/*    <div
                        className="relative z-0 inline-flex -space-x-px "
                        aria-label="Pagination"
                      >
                        <button
                          className="rounded-l-md"
                          onClick={() => gotoPage(0)}
                          disabled={!canPreviousPage}
                        >
                          <span className="sr-only">First</span>
                        </button>
                        <button
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeftIcon
                            className="h-5 w-5 text-blue-900 cursor-pointer"
                            aria-hidden="true"
                          />
                        </button>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-blue-700 font-medium font-Roboto">
                            <span className="font-medium">
                              {state.pageIndex + 1}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">
                              {pageOptions.length}
                            </span>
                          </span>
                        </div>
                        <button
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRightIcon
                            className="h-5 w-5 text-blue-900 cursor-pointer"
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          className="rounded-r-md"
                          onClick={() => gotoPage(pageCount - 1)}
                          disabled={!canNextPage}
                        >
                          <span className="sr-only">Last</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </tfoot>*/}
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BomTable;
